import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "project-orders" }
const _hoisted_2 = { class: "table__footer" }
const _hoisted_3 = {
  key: 1,
  class: "empty"
}

import { computed, ref, reactive, watch } from "vue";
import { OnClickOutside } from "@vueuse/components";
import dayjs from "dayjs";
import { useQuery } from "@vue/apollo-composable";
import { useI18n } from "vue-i18n";
import { GET_PROJECT_INVOICES } from "@/graphql/invoice/queries";
import { type PaginatedResult } from "@/helpers/typesDefinition";
import { type Invoice } from "@/helpers/invoice/typesDefinition";
import { type Order } from "@/helpers/order/typesDefinition";
import { formatPrice } from "@/helpers/utils";
import { formatPeriodDateShort } from "@/helpers/order/utils";
import TableBase, { type TableBaseColumn } from "@/components/TableBase.vue";
import DropDownItem from "@/components/dropDownMenu/DropDownItem.vue";
import DropDownMenu from "@/components/dropDownMenu/DropDownMenu.vue";
import config from "@/config";
import BasicButton from "@/components/BasicButton.vue";
import OrderDetails from "@/components/order/OrderDetails.vue";
import checkHasAccess from "@/helpers/permissions/checkHasAccess";
import { GET_PROJECT_FOR_ACCESS_RIGHTS } from "@/graphql/project/queries";
import { useToast } from "vue-toastification";

type GetProjectInvoicesResult = {
  projectInvoices: PaginatedResult<Invoice>;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectInvoices',
  props: {
    id: {}
  },
  setup(__props: any) {

const backEndUri = config.backendUri;

const props = __props;

const { t } = useI18n({
  inheritLocale: true,
  useScope: "global",
});
const toast = useToast();

const getProjectInvoicesVariables = computed(() => {
  const projectId = props.id;
  if (projectId) {
    return {
      offset: 0,
      projectId,
    };
  }
  return {};
});

const { result: invoicesResult, fetchMore } =
  useQuery<GetProjectInvoicesResult>(
    GET_PROJECT_INVOICES,
    getProjectInvoicesVariables,
  );

const invoices = computed(() => {
  return invoicesResult.value?.projectInvoices.items;
});

const hasMore = computed(() => {
  return invoicesResult.value?.projectInvoices?.hasMore;
});

const { result: projectResult } = useQuery(GET_PROJECT_FOR_ACCESS_RIGHTS, {
  id: props.id,
});

const loadMore = () => {
  if (hasMore.value) {
    fetchMore({
      variables: {
        projectId: props.id,
        offset: invoices.value?.length,
      },
    });
  }
};

const hasAdminAccess = ref(false);
const hasClientAccess = ref(false);
const readOnly = ref(true);

watch([projectResult], async ([projectResult]) => {
  const project = projectResult.value?.project;
  if (!project) return;
  readOnly.value = project.active;
  hasAdminAccess.value = await checkHasAccess(
    project.client.id,
    props.id,
    "admin",
    toast,
    t("errors.getUserDetails"),
  );
  hasClientAccess.value = await checkHasAccess(
    project.client.id,
    props.id,
    "client",
    toast,
    t("errors.getUserDetails"),
  );
});

const openedOrderId = ref("");
const isOrderDetailsModalVisible = computed(() => Boolean(openedOrderId.value));
const selectOrder = (orderId: string) => {
  openedOrderId.value = orderId;
};

function closeOrderDetailsModal(): void {
  openedOrderId.value = "";
}

const columns: TableBaseColumn[] = [
  {
    name: "invoiceNumber",
    title: t("labels.invoices.invoiceNumber"),
  },
  {
    name: "period",
    title: t("labels.invoices.period"),
  },
  {
    name: "date",
    title: t("labels.invoices.date"),
  },
  {
    name: "paymentOptionLabel",
    title: t("labels.order.paymentOption"),
  },
  {
    name: "companyName",
    title: t("labels.invoices.company"),
  },
  {
    name: "netTotal",
    title: t("labels.invoices.netTotal"),
  },
  {
    name: "total",
    title: t("labels.invoices.total"),
  },
  {
    name: "balance",
    title: t("labels.invoices.balance"),
    isImportant: true,
  },
  {
    name: "dueDate",
    title: t("labels.invoices.dueDate"),
  },
  {
    name: "status",
    title: t("labels.order.status"),
  },
];

const formattedInvoices = computed(() => {
  if (!invoices.value?.length) return [];
  return invoices.value?.map((invoice) => {
    const linkedOrder =
      invoice.linkedResource?.__typename === "Order"
        ? (invoice.linkedResource as Order)
        : null;
    const paymentOptionLabel = linkedOrder?.paymentOption
      ? t(`paymentOptionLabels.${linkedOrder.paymentOption}`)
      : t("paymentOptionLabels.post_paid");
    return {
      ...invoice,
      period: invoice.linkedResource
        ? formatPeriodDateShort(invoice.linkedResource)
        : null,
      companyName: invoice.company.name,
      date: dayjs(invoice.date).format("DD/MM/YYYY"),
      netTotal: formatPrice(invoice.netTotal),
      total: formatPrice(invoice.total),
      balance:
        invoice.balance > 0
          ? {
              className: "text-danger",
              value: formatPrice(invoice.balance),
            }
          : formatPrice(invoice.balance),
      dueDate: dayjs(invoice.dueDate).format("DD/MM/YYYY"),
      status: invoice.invoiceStatus.startsWith("reminder")
        ? {
            className: "text-danger",
            value: t(`statusLabels.${invoice.invoiceStatus}`),
          }
        : t(`statusLabels.${invoice.invoiceStatus}`),
      paymentOptionLabel,
    };
  });
});

const sortedInvoices = computed(() => {
  const invoices = [...formattedInvoices.value];
  return invoices.sort((invoiceA, invoiceB) => {
    if (!invoiceA.linkedResource || !invoiceB.linkedResource) {
      return dayjs(invoiceB.date).diff(invoiceA.date);
    }
    if (invoiceA.type === "order" && invoiceB.type !== "order") {
      // For the same period, display delta above retainer
      return 1;
    }
    const dateStartA = dayjs(invoiceA.linkedResource?.startDate);
    const dateStartB = dayjs(invoiceB.linkedResource?.startDate);
    return dateStartB.diff(dateStartA);
  });
});

const activeDropdown = ref("");
const isDropdownActive = (id: string) => activeDropdown.value === id;

const references = reactive<Record<string, HTMLElement | null>>({});

const getRef = (invoiceId: string, element: HTMLElement | null) => {
  references[invoiceId] = element;
};
const toggleDropdown = (dropdownId: string) => {
  const newActiveDropdown =
    activeDropdown.value === dropdownId ? "" : dropdownId;
  activeDropdown.value = newActiveDropdown;
};

const handleInvoiceClick = (id: string) => {
  toggleDropdown(id);
};

const closeDropdown = () => toggleDropdown("");

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(TableBase, {
        columns: columns,
        items: sortedInvoices.value
      }, {
        actions: _withCtx(({ item: invoice }) => [
          _createElementVNode("div", {
            class: "cell cell--actions",
            ref: (el) => getRef(invoice.id, el as HTMLElement | null)
          }, [
            _createVNode(_unref(OnClickOutside), { onTrigger: closeDropdown }, {
              default: _withCtx(() => [
                _createVNode(BasicButton, {
                  onClick: () => handleInvoiceClick(invoice.id)
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_unref(t)("dropDownMenu.button.label")), 1)
                  ]),
                  _: 1
                }, 8, ["onClick"])
              ]),
              _: 2
            }, 1024),
            (isDropdownActive(invoice.id))
              ? (_openBlock(), _createBlock(DropDownMenu, {
                  key: 0,
                  reference: references[invoice.id]
                }, {
                  default: _withCtx(() => [
                    _createVNode(DropDownItem, {
                      label: _unref(t)('dropDownMenu.download.invoice'),
                      iconName: "akar-icons:download",
                      href: `${_unref(backEndUri)}/documents/invoice/${invoice.id}`,
                      target: "_blank"
                    }, null, 8, ["label", "href"]),
                    (invoice.hasTimesheets)
                      ? (_openBlock(), _createBlock(DropDownItem, {
                          key: 0,
                          label: _unref(t)('dropDownMenu.download.timesheets'),
                          iconName: "akar-icons:download",
                          href: `${_unref(backEndUri)}/documents/reports/draft-invoice/${invoice.id}`,
                          target: "_blank"
                        }, null, 8, ["label", "href"]))
                      : _createCommentVNode("", true),
                    (invoice.type === 'order')
                      ? (_openBlock(), _createBlock(DropDownItem, {
                          key: 1,
                          label: _unref(t)('dropDownMenu.seeOrder'),
                          iconName: "eva:eye-fill",
                          onClick: ($event: any) => (selectOrder(invoice.linkedResource?.id))
                        }, null, 8, ["label", "onClick"]))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1032, ["reference"]))
              : _createCommentVNode("", true)
          ], 512)
        ]),
        _: 1
      }, 8, ["items"]),
      _createElementVNode("div", _hoisted_2, [
        (hasMore.value)
          ? (_openBlock(), _createBlock(BasicButton, {
              key: 0,
              onClick: loadMore
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("buttons.invoices.loadMore")), 1)
              ]),
              _: 1
            }))
          : (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(_ctx.$t("invoices.allLoaded")), 1))
      ])
    ]),
    (isOrderDetailsModalVisible.value)
      ? (_openBlock(), _createBlock(OrderDetails, {
          key: 0,
          id: openedOrderId.value,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (closeOrderDetailsModal())),
          projectId: __props.id,
          "has-client-access": hasClientAccess.value,
          hasAdminAccess: hasAdminAccess.value,
          "read-only": readOnly.value
        }, null, 8, ["id", "projectId", "has-client-access", "hasAdminAccess", "read-only"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})